import {
  useMemo,
  useState,
  type FocusEvent,
  type InputHTMLAttributes
} from "react";
import { twMerge } from "tailwind-merge";

type InputWithDropdownProps = {
  readonly label: string;
  readonly value: string;
  readonly options: readonly string[];
  readonly placeholder?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly className?: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "inputMode" | "maxLength" | "pattern" | "type"
>;

export const InputWithDropdown = ({
  label,
  value,
  options,
  placeholder,
  error,
  required = false,
  disabled = false,
  id,
  className,
  onChange,
  onBlur,
  inputMode,
  maxLength,
  pattern,
  type = "text"
}: InputWithDropdownProps) => {
  const inputId = id ?? label.toLowerCase().replaceAll(/\s+/g, "-");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return options;
    }
    return options.filter((option) =>
      option.toLowerCase().includes(normalizedValue)
    );
  }, [options, value]);

  const shouldShowDropdown = isOpen && !disabled && filteredOptions.length > 0;

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
      onBlur?.();
    }
  };

  return (
    <div
      className={twMerge("relative space-y-1.5", className)}
      onBlur={handleBlur}
    >
      <label htmlFor={inputId} className="form-label">
        {label}
        {required && <span className="form-label-asterisk">*</span>}
      </label>
      <input
        id={inputId}
        type={type}
        value={value}
        inputMode={inputMode}
        maxLength={maxLength}
        pattern={pattern}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-autocomplete="list"
        aria-expanded={shouldShowDropdown}
        className={twMerge(
          "w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
          error ? "border-red-400" : "border-gray-300",
          disabled && "cursor-not-allowed bg-gray-100 text-gray-500"
        )}
      />
      {shouldShowDropdown && (
        <div
          className="absolute z-20 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={option === value}
            >
              {option}
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
